import parse from 'date-fns/parse';
import { rest } from '@xolv-mybrightlink/common';

const DATE_FORMAT = 'yyyy-MM-dd';

export type Patient = {
  id: number;
  guid: string;
  sfId: number;
  firstName: string;
  lastName: string;
  gender: string;
  dateOfBirth: Date | null;
  userRelationship: string;
  strengths: string;
  communicationLevel: string;
  interactionLevel: string;
  selfCareAssistanceLevel: string;
  mrn: string;
  age: number | null;
};

export type UserProfile = {
  id: number;
  guid: string;
  firstName: string;
  lastName: string;
  fullName: string;
  biography: string;
  formattedAddress: string;
  city: string;
  zipCode: string;
  state: string;
  country: string;
  locationLongitude: number;
  locationLatitude: number;
  interests: string[];
  patients: Patient[];
};

export type Location = {
  place_id: string;
  description: string;
};

export type UpdateFn<T, K extends keyof T> = (
  key: K,
  fn: (_: Partial<T>) => T[K] | T[K][],
) => void;

export type PartialUpdateFn<T> = UpdateFn<T, keyof T>;

function tryParse(dob: string): Date | null {
  try {
    return parse(dob, DATE_FORMAT, new Date());
  } catch (_: unknown) {
    return null;
  }
}

export async function getProfile(): Promise<UserProfile | null> {
  try {
    const { data: userProfileData } = await rest.get<Record<string, unknown>>(
      '/user/profile',
    );
    return {
      id: userProfileData['id'],
      guid: userProfileData['guid'],
      firstName: userProfileData['first_name'],
      lastName: userProfileData['last_name'],
      fullName: `${userProfileData['first_name']} ${userProfileData['last_name']}`,
      biography: userProfileData['biography'],
      formattedAddress: userProfileData['formatted_address'],
      city: userProfileData['city'],
      zipCode: userProfileData['zip_code'],
      state: userProfileData['state'],
      country: userProfileData['country'],
      locationLongitude: userProfileData['longitude'],
      locationLatitude: userProfileData['latitude'],
      interests: userProfileData['interests'] || [],
      patients: ((userProfileData['patients'] || []) as never[]).map(
        (patientData: { [x: string]: unknown }) => ({
          id: patientData['id'],
          guid: patientData['guid'],
          firstName: patientData['first_name'],
          lastName: patientData['last_name'],
          gender: patientData['gender'],
          dateOfBirth: tryParse(patientData['date_of_birth'] as string),
          strengths: patientData['strengths'],
          communicationLevel: patientData['communication_level'],
          interactionLevel: patientData['interaction_level'],
          selfCareAssistanceLevel: patientData['self_care_assistance_level'],
        }),
      ) as Patient[],
    } as UserProfile;
  } catch (e: unknown) {
    console.warn((e as Error).message);
    return null;
  }
}

export async function updateProfile(
  userProfile: Partial<UserProfile>,
): Promise<number> {
  const profileData = {
    first_name: userProfile.firstName,
    last_name: userProfile.lastName,
    biography: userProfile.biography,
    formatted_address: userProfile.formattedAddress,
    city: userProfile.city,
    zip_code: userProfile.zipCode,
    state: userProfile.state,
    country: userProfile.country,
    longitude: userProfile.locationLongitude,
    latitude: userProfile.locationLatitude,
    interests: userProfile.interests,
    patients: (userProfile?.patients ?? []).map((patient: Patient) => ({
      id: patient.id,
      strengths: patient.strengths,
      communication_level: patient.communicationLevel,
      interaction_level: patient.interactionLevel,
      self_care_assistance_level: patient.selfCareAssistanceLevel,
    })),
    profile_setup_completed: true,
  };

  const response = await rest.post('/user/profile', profileData);

  return response.data['profile_id'];
}

export async function searchLocation(
  input: string,
): Promise<readonly Location[]> {
  const { data } = await rest.get<Location[]>(
    `/gg/places/auto-complete?input=${input}`,
  );
  return data;
}
